import { useCallback } from "react";
import apiClient from "../../api/axios";
import { FlowState, JobStatus, FileWithStatus } from "./types";
import { MAX_RETRIES, RETRY_DELAY_MS } from "./constants";

export const useGeneralExtraction = (
  jobStatuses: Record<string, JobStatus>,
  fileLists: { valid: FileWithStatus[]; invalid: FileWithStatus[] },
  filesWereDeleted: boolean,
  setError: (error: string | null) => void,
  setFlowState: (state: FlowState) => void,
  setJobStatuses: (updater: (prev: Record<string, JobStatus>) => Record<string, JobStatus>) => void,
  setTotalFilesForProgressBar: (total: number) => void,
  setCompletedFilesForProgressBar: (completed: number) => void,
  setIsSummaryDataReady: (ready: boolean) => void,
  setFailedUploads: (files: string[]) => void
) => {
  const handleExtractGeneral = useCallback(async () => {
    // Determine which files have already been processed (completed or errored)
    const alreadyProcessedFileIds = new Set<string>();
    Object.values(jobStatuses).forEach((job) => {
      if (job.status === "complete" || job.status === "error") {
        alreadyProcessedFileIds.add(job.fileId);
      }
    });

    const filesToProcess = fileLists.valid.filter(
      (fileWithStatus) => !alreadyProcessedFileIds.has(fileWithStatus.id)
    );

    if (filesToProcess.length === 0) {
      // If files were deleted, the verification result might have changed, so show the summary.
      // Otherwise, nothing has changed, so just transition back to the results screen.
      if (filesWereDeleted) {
        setIsSummaryDataReady(true);
      } else {
        setFlowState("generalExtracted");
      }
      return;
    }

    const alreadyProcessedJobs = Object.values(jobStatuses).filter(
      (j) => j.status === "complete" || j.status === "error"
    );
    const newTotal = filesToProcess.length + alreadyProcessedJobs.length;

    setError(null);
    setTotalFilesForProgressBar(newTotal);
    setCompletedFilesForProgressBar(alreadyProcessedJobs.length);
    setIsSummaryDataReady(false); // Reset summary data readiness
    setFlowState("processing");

    const initialStatuses: Record<string, JobStatus> = {};
    const failedFiles: string[] = [];
    const failedJobStatuses: Record<string, JobStatus> = {};

    const uploadFileWithRetry = async (file: File): Promise<string | null> => {
      // Generate UUID for this file
      const fileUuid = crypto.randomUUID();

      const formData = new FormData();
      formData.append("file", file);

      for (let i = 0; i <= MAX_RETRIES; i++) {
        try {
          const uploadResponse = await apiClient.post(
            `/api/upload-file/?original_filename=${encodeURIComponent(file.name)}&file_uuid=${fileUuid}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          return uploadResponse.data.job_id;
        } catch (err) {
          console.error(
            `Error uploading file ${file.name} (attempt ${i + 1}/${
              MAX_RETRIES + 1
            }):`,
            err
          );
          if (i < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          }
        }
      }
      return null; // All retries failed
    };

    // ステップ1: PDF変換を逐次処理（asyncio.Lockの競合を回避）
    const convertedFiles = new Map<string, File>();
    for (const fileWithStatus of filesToProcess) {
      let fileToProcess = fileWithStatus.file;
      const needsConversion =
        fileToProcess.type.includes("msword") ||
        fileToProcess.type.includes(
          "openxmlformats-officedocument.wordprocessingml.document"
        ) ||
        fileToProcess.type.includes("ms-excel") ||
        fileToProcess.type.includes(
          "openxmlformats-officedocument.spreadsheetml.sheet"
        ) ||
        fileToProcess.type.includes("csv");

      if (needsConversion) {
        try {
          const convertFormData = new FormData();
          convertFormData.append("file", fileToProcess);
          const response = await apiClient.post(
            "/api/convert-to-pdf",
            convertFormData,
            {
              responseType: "blob",
              timeout: 300000, // 5分のタイムアウト
            }
          );
          const originalName = fileToProcess.name.substring(
            0,
            fileToProcess.name.lastIndexOf(".")
          );
          fileToProcess = new File([response.data], `${originalName}.pdf`, {
            type: "application/pdf",
          });
          convertedFiles.set(fileWithStatus.id, fileToProcess);
        } catch (err) {
          console.error(`Failed to convert ${fileToProcess.name} to PDF`, err);
          failedFiles.push(fileWithStatus.file.name);
          failedJobStatuses[`failed-${fileWithStatus.id}`] = {
            file: fileWithStatus.file,
            fileId: fileWithStatus.id,
            status: "error",
            data: null,
            error: `${fileWithStatus.file.name} のPDF変換に失敗しました。`,
          };
        }
      } else {
        convertedFiles.set(fileWithStatus.id, fileToProcess);
      }
    }

    // ステップ2: 変換済みファイルをバッチアップロード（DB接続数対策）
    const UPLOAD_BATCH_SIZE = 5;
    const filesToUpload = filesToProcess.filter((f) => convertedFiles.has(f.id));

    for (let i = 0; i < filesToUpload.length; i += UPLOAD_BATCH_SIZE) {
      const batch = filesToUpload.slice(i, i + UPLOAD_BATCH_SIZE);
      const uploadPromises = batch.map(async (fileWithStatus) => {
        const fileToProcess = convertedFiles.get(fileWithStatus.id)!;

        const job_id = await uploadFileWithRetry(fileToProcess);
        if (job_id) {
          initialStatuses[job_id] = {
            file: fileToProcess,
            fileId: fileWithStatus.id,
            status: "processing",
            data: null,
          };
        } else {
          failedFiles.push(fileWithStatus.file.name);
          failedJobStatuses[`failed-${fileWithStatus.id}`] = {
            file: fileWithStatus.file,
            fileId: fileWithStatus.id,
            status: "error",
            data: null,
            error: `${fileWithStatus.file.name} のアップロードに失敗しました。`,
          };
        }
      });

      await Promise.all(uploadPromises);
    }
    
    setJobStatuses((prev) => ({
      ...prev,
      ...initialStatuses,
      ...failedJobStatuses,
    }));

    if (failedFiles.length > 0) {
      setFailedUploads(failedFiles);
    }
  }, [
    jobStatuses,
    fileLists.valid,
    filesWereDeleted,
    setError,
    setFlowState,
    setJobStatuses,
    setTotalFilesForProgressBar,
    setCompletedFilesForProgressBar,
    setIsSummaryDataReady,
    setFailedUploads,
  ]);

  return {
    handleExtractGeneral,
  };
};